import lowerCase from 'lodash/lowerCase';

import { AutocompleteOption } from 'domains/core/components/Autocomplete/Autocomplete';
import InputLabel from 'domains/core/components/InputLabel';
import Select, { Value } from 'domains/core/components/Select';
import FilterValueInput from 'domains/segments/pages/SegmentCreatePage/Filter/FilterValueInput';
import {
    getInitialDateValue,
    getNumericOrStringValue,
    getOperatorValues,
} from 'domains/segments/pages/SegmentCreatePage/Filter/utils';
import {
    DemographicContainsOperator,
    DemographicContainsOperatorHelpText,
    NullOperator,
    SegmentDemographicValue,
    SegmentFilter,
    SegmentFilterDemographicFields,
    SegmentFilterState,
    isMultiInputOperator as isMultiInputOperatorType,
} from 'domains/segments/types';
import { SQLType } from 'models/enums';

export type Props = {
    filterOptions: SegmentFilter[];
    filter: SegmentFilterState<SegmentFilterDemographicFields>;
    index: number;
    handleDatetimeError: (filterId: string, error: boolean) => void;
    updateSegmentFilter: (
        index: number,
        filter: SegmentFilterState<SegmentFilterDemographicFields>,
        resetPreview?: boolean
    ) => void;
};

export type DemographicFilterComponents = {
    filterSelect: React.ReactNode;
    operatorSelect: React.ReactNode | null;
    valueInput: React.ReactNode | null;
};

const DemographicFilter = ({ filterOptions, filter, index, handleDatetimeError, updateSegmentFilter }: Props) => {
    const { categorySpecificFields, name, operator } = filter;
    const { value } = categorySpecificFields;
    const filterOption = filterOptions?.find((filterOption) => filterOption.name === name);

    const filterOptionsSorted: Value[] = filterOptions
        ?.sort((a, b) => a.displayName.localeCompare(b.displayName))
        .map((filter) => ({ label: lowerCase(filter.displayName), value: filter.name }));

    const isValidFilterName = filterOptionsSorted.map((filterOptionSorted) => filterOptionSorted.value).includes(name);
    const selectedName = isValidFilterName ? name : '';

    const operatorValues = getOperatorValues(filterOption?.sqlType);
    const operatorsHaveHelpText = filterOption?.sqlType === SQLType.CHARACTER_VARYING_ARRAY;
    const operatorValuesWithHelpText = operatorValues.map((operator) => ({
        label: (
            <InputLabel
                label={operator.label}
                labelIconTooltipText={
                    DemographicContainsOperatorHelpText[operator.label as DemographicContainsOperator]
                }
            />
        ),
        value: operator.value,
    }));
    const operators = operatorsHaveHelpText ? operatorValuesWithHelpText : operatorValues;

    const handleFilterChange = (e: any) => {
        const { autocompleteInputValue, autocompleteValue, ...restOfFilter } = filter;

        const newValue =
            filterOption?.sqlType === 'date' || filterOption?.sqlType === 'timestamp with time zone'
                ? getInitialDateValue(value)
                : '';

        updateSegmentFilter(index, {
            ...restOfFilter,
            categorySpecificFields: {
                value: newValue,
            },
            name: e.target.value,
            operator: null,
        });
    };

    const handleOperatorChange = (e: any) => {
        const { autocompleteInputValue, autocompleteValue, ...restOfFilter } = filter;
        const isMultiInputOperator = isMultiInputOperatorType(e.target.value);

        updateSegmentFilter(index, {
            ...restOfFilter,
            ...(isMultiInputOperator && { autocompleteValue: [] }),
            autocompleteInputValue: '',
            categorySpecificFields: {
                value: isMultiInputOperator ? [] : '',
            },
            operator: e.target.value,
        });
    };

    const handleChangeValue = (value: any) => {
        const { autocompleteInputValue, autocompleteValue, ...restOfFilter } = filter;
        const newValue = getNumericOrStringValue(value, filterOption?.sqlType);

        updateSegmentFilter(index, {
            ...restOfFilter,
            autocompleteInputValue: `${newValue}`,
            categorySpecificFields: {
                value: newValue,
            },
        });
    };

    const handleChangeAutocompleteValue = (
        autocompleteValue: any | AutocompleteOption<string>[],
        resetPreview?: boolean
    ) => {
        const getOption = (value: string | AutocompleteOption<string>) => {
            if (!!value && typeof value === 'object' && 'label' in value && 'value' in value) return value;
            return { label: value as string, value: value as string };
        };

        const hasMultipleValues = Array.isArray(autocompleteValue);

        const newAutocompleteInputValue = hasMultipleValues ? '' : autocompleteValue?.value ?? autocompleteValue;

        const newAutocompleteValue: AutocompleteOption<string> | AutocompleteOption<string>[] = hasMultipleValues
            ? autocompleteValue.map((autocompleteValue) => getOption(autocompleteValue))
            : getOption(autocompleteValue);

        const newValue = hasMultipleValues
            ? (newAutocompleteValue as AutocompleteOption<string>[]).map((autocompleteValue) =>
                  getNumericOrStringValue(autocompleteValue?.value, filterOption?.sqlType)
              )
            : getNumericOrStringValue(newAutocompleteInputValue, filterOption?.sqlType);

        updateSegmentFilter(
            index,
            {
                ...filter,
                categorySpecificFields: {
                    value: newValue as SegmentDemographicValue,
                },
                autocompleteInputValue: newAutocompleteInputValue,
                autocompleteValue: newAutocompleteValue,
            },
            resetPreview
        );
    };

    const handleChangeAutocompleteInputValue = (autocompleteInputValue: string, hasMultipleValues: boolean) => {
        const value = !hasMultipleValues && {
            categorySpecificFields: {
                value: autocompleteInputValue,
            },
        };

        updateSegmentFilter(index, {
            ...filter,
            ...value,
            autocompleteInputValue,
        });
    };

    // Return individual components for flexible layout
    const components: DemographicFilterComponents = {
        filterSelect: (
            <Select
                data-test="details-filter-select"
                label="Filter"
                sx={{ textTransform: 'capitalize', width: '14rem' }}
                value={selectedName}
                values={filterOptionsSorted}
                onChange={handleFilterChange}
            />
        ),
        operatorSelect: selectedName ? (
            <Select
                data-test="details-operator-select"
                label="Operator"
                sx={{ textTransform: 'capitalize', width: '12rem' }}
                value={operator ?? ''}
                values={operators}
                onChange={handleOperatorChange}
            />
        ) : null,
        valueInput:
            operator && filterOption && operator !== NullOperator.IS_NOT_NULL && operator !== NullOperator.IS_NULL ? (
                <FilterValueInput
                    filterOption={filterOption}
                    filterState={filter}
                    handleDatetimeError={handleDatetimeError}
                    handleChangeValue={handleChangeValue}
                    handleChangeAutocompleteValue={handleChangeAutocompleteValue}
                    handleChangeAutocompleteInputValue={handleChangeAutocompleteInputValue}
                />
            ) : null,
    };

    return components;
};

export default DemographicFilter;
